import { styled, Theme } from "@mui/material/styles";

import AbandonedSvg from "./icons/abandoned.svg?react";
import AddSvg from "./icons/add.svg?react";
import BackSvg from "./icons/back.svg?react";
import CalendarSvg from "./icons/calendar.svg?react";
import CaretUpSvg from "./icons/caret-up.svg?react";
import CaretSvg from "./icons/caret.svg?react";
import CheckSvg from "./icons/check.svg?react";
import CloseSvg from "./icons/close.svg?react";
import CopySvg from "./icons/copy.svg?react";
import CriticalWarningSvg from "./icons/critical-warning.svg?react";
import DisconnectSvg from "./icons/disconnect.svg?react";
import EditSvg from "./icons/edit.svg?react";
import ExportSvg from "./icons/export.svg?react";
import FavoriteSvg from "./icons/favorite.svg?react";
import FileSvg from "./icons/file.svg?react";
import FilterSvg from "./icons/filter.svg?react";
import ForwardSvg from "./icons/forward.svg?react";
import InfoSvg from "./icons/info.svg?react";
import LeftSvg from "./icons/left.svg?react";
import NoDataSvg from "./icons/no-data.svg?react";
import PlusSvg from "./icons/plus.svg?react";
import RefreshSvg from "./icons/refresh.svg?react";
import RightSvg from "./icons/right.svg?react";
import SearchSvg from "./icons/search.svg?react";
import SendSvg from "./icons/send.svg?react";
import SensorSvg from "./icons/sensor.svg?react";
import SuccessSvg from "./icons/success.svg?react";
import TrashSvg from "./icons/trash.svg?react";
import WarningSvg from "./icons/warning.svg?react";

type IconName =
  | "abandoned"
  | "add"
  | "back"
  | "calendar"
  | "caret"
  | "caret-up"
  | "check"
  | "close"
  | "copy"
  | "critical-warning"
  | "disconnect"
  | "edit"
  | "export"
  | "favorite"
  | "file"
  | "filter"
  | "forward"
  | "info"
  | "left"
  | "no-data"
  | "plus"
  | "refresh"
  | "right"
  | "search"
  | "send"
  | "sensor"
  | "success"
  | "trash"
  | "warning";

type IconSize = "sm" | "md" | "lg";

type IconColor = "inherit" | "primary";

type Props = {
  name: IconName;
  size?: IconSize;
  color?: IconColor;
  "aria-label"?: string;
  className?: string;
  "data-testid"?: string;
};

const Icon = ({
  "aria-label": ariaLabel,
  className,
  color = "inherit",
  "data-testid": dataTestId,
  name,
  size = "md",
}: Props): React.ReactElement => {
  const Svg = ICONS[name];

  return (
    <Root
      $color={color}
      $size={size}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      className={className}
      data-testid={dataTestId ?? `icon-${name}`}
      role={ariaLabel ? "img" : undefined}
    >
      <Svg />
    </Root>
  );
};

const SIZE_PX: Record<IconSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
};

const ICONS: Record<IconName, React.FunctionComponent> = {
  abandoned: AbandonedSvg,
  add: AddSvg,
  back: BackSvg,
  calendar: CalendarSvg,
  caret: CaretSvg,
  "caret-up": CaretUpSvg,
  check: CheckSvg,
  close: CloseSvg,
  copy: CopySvg,
  "critical-warning": CriticalWarningSvg,
  disconnect: DisconnectSvg,
  edit: EditSvg,
  export: ExportSvg,
  favorite: FavoriteSvg,
  file: FileSvg,
  filter: FilterSvg,
  forward: ForwardSvg,
  info: InfoSvg,
  left: LeftSvg,
  "no-data": NoDataSvg,
  plus: PlusSvg,
  refresh: RefreshSvg,
  right: RightSvg,
  search: SearchSvg,
  send: SendSvg,
  sensor: SensorSvg,
  success: SuccessSvg,
  trash: TrashSvg,
  warning: WarningSvg,
};

const ICON_NAMES = Object.keys(ICONS) as IconName[];

const ICON_COLORS: Record<
  Exclude<IconColor, "inherit">,
  (theme: Theme) => string
> = {
  primary: (theme) => theme.palette.cf.cfPrimary,
};

const Root = styled("span", {
  shouldForwardProp: (prop) => prop !== "$size" && prop !== "$color",
})<{ $size: IconSize; $color: IconColor }>(({ $color, $size, theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  flexShrink: 0,
  width: SIZE_PX[$size],
  height: SIZE_PX[$size],
  minWidth: SIZE_PX[$size],
  minHeight: SIZE_PX[$size],
  color: $color === "inherit" ? "inherit" : ICON_COLORS[$color](theme),
  "& > svg": {
    width: "100%",
    height: "100%",
    display: "block",
  },
}));

export { Icon, ICON_NAMES, type IconColor, type IconName, type IconSize };
